/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useId, useState } from 'react';
import { Label, Text, TextInput, UnstyledButton } from '@/shared/ui';
import {
  isSuggestQuery,
  matchesASuggestion,
  useFieldSuggestions,
  type FieldSuggestion,
  type SuggestionFetcher,
} from './useFieldSuggestions';

export interface FieldSuggestInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Called after a stored value is chosen (not for the free-text option). */
  onPick?: (suggestion: FieldSuggestion) => void;
  onBlur?: () => void;
  fetcher: SuggestionFetcher;
  /** The line under a suggestion: how many records use it, and with what. */
  describe: (suggestion: FieldSuggestion) => string;
  /** The last option, keeping what was typed as a new value. */
  newValueLabel: (typed: string) => string;
  placeholder?: string;
  maxLength?: number;
  labelStyle?: React.CSSProperties;
  testId?: string;
}

/** A row of the list: a stored value, or the typed text kept as new. */
type SuggestOption = { kind: 'stored'; suggestion: FieldSuggestion } | { kind: 'new' };

/**
 * The rows on offer: the stored values, then "use as new" unless the typed
 * text already is one of them.
 *
 * @param typed - The text in the field.
 * @param items - The stored values.
 * @returns The rows, in display order.
 */
function buildOptions(typed: string, items: FieldSuggestion[]): SuggestOption[] {
  const stored: SuggestOption[] = items.map((suggestion) => ({ kind: 'stored', suggestion }));
  if (!isSuggestQuery(typed) || matchesASuggestion(typed, items)) return stored;
  return [...stored, { kind: 'new' }];
}

/**
 * The next highlighted row for an arrow key, wrapping at both ends.
 *
 * @param key - The key pressed.
 * @param active - The row highlighted now (-1 for none).
 * @param count - How many rows there are.
 * @returns The new highlighted row, or null when the key is not an arrow.
 */
function nextActive(key: string, active: number, count: number): number | null {
  if (key === 'ArrowDown') return (active + 1) % count;
  if (key === 'ArrowUp') return (active <= 0 ? count : active) - 1;
  return null;
}

interface OptionRowProps {
  id: string;
  option: SuggestOption;
  active: boolean;
  typed: string;
  describe: FieldSuggestInputProps['describe'];
  newValueLabel: FieldSuggestInputProps['newValueLabel'];
  onChoose: (option: SuggestOption) => void;
}

/**
 * One row of the list. Chosen on mouse down so the input's blur does not
 * close the list first.
 *
 * @param props - The row, its state and the choose callback.
 * @returns The row.
 */
function OptionRow({ id, option, active, typed, describe, newValueLabel, onChoose }: OptionRowProps) {
  const isStored = option.kind === 'stored';
  return (
    <UnstyledButton
      id={id}
      type="button"
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onMouseDown={(event: React.MouseEvent) => {
        event.preventDefault();
        onChoose(option);
      }}
      className={`flex w-full flex-col items-start gap-(--space-0-5) border-none px-(--space-3) py-(--space-2) text-left cursor-pointer hover:bg-(--surface-hover) ${active ? 'bg-(--surface-hover)' : 'bg-transparent'}`}
    >
      <Text
        variant="bodySm"
        component="span"
        className={`text-(--text-primary) ${isStored ? '[font-weight:var(--font-semibold)]' : ''}`}
      >
        {isStored ? option.suggestion.value : newValueLabel(typed.trim())}
      </Text>
      {isStored && (
        <Text variant="meta" component="span" className="text-(--text-tertiary-soft)">
          {describe(option.suggestion)}
        </Text>
      )}
    </UnstyledButton>
  );
}

/**
 * Free-text field that suggests the values the agency already used.
 *
 * Typing two letters or more lists the stored values that contain them,
 * most used first, each with how many records hold it and what they share.
 * The last row keeps the typed text as a new value: nothing is created, it
 * stays plain text. Arrow keys move, Enter chooses, Escape closes.
 *
 * @param props - Field label, controlled value, the fetcher and the copy.
 * @returns The input with its suggestion list.
 */
export function FieldSuggestInput({
  label, value, onChange, onPick, onBlur, fetcher, describe, newValueLabel,
  placeholder, maxLength, labelStyle, testId,
}: FieldSuggestInputProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const { items } = useFieldSuggestions(value, fetcher, open);
  const options = open ? buildOptions(value, items) : [];
  const expanded = options.length > 0;

  const choose = (option: SuggestOption) => {
    if (option.kind === 'stored') {
      onChange(option.suggestion.value);
      onPick?.(option.suggestion);
    }
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!expanded) return;
    const next = nextActive(event.key, active, options.length);
    if (next !== null) {
      event.preventDefault();
      setActive(next);
      return;
    }
    handleCommitKey(event, options[active]);
  };

  const handleCommitKey = (event: React.KeyboardEvent, highlighted?: SuggestOption) => {
    if (event.key === 'Escape') setOpen(false);
    if (event.key !== 'Enter' || !highlighted) return;
    event.preventDefault();
    choose(highlighted);
  };

  return (
    <div className="flex flex-col" data-testid={testId}>
      <Label style={labelStyle}>{label}</Label>
      <div className="relative">
        <TextInput
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            onBlur?.();
          }}
          onKeyDown={onKeyDown}
        />
        {expanded && (
          <div
            id={listId}
            role="listbox"
            className="absolute left-0 right-0 top-full z-50 mt-(--space-1) max-h-[16rem] overflow-y-auto rounded-(--radius-md) border border-solid border-(--border-subtle) bg-(--surface-primary) py-(--space-1) shadow-(--shadow-md)"
          >
            {options.map((option, index) => (
              <OptionRow
                key={option.kind === 'stored' ? option.suggestion.value : '__new__'}
                id={`${listId}-${index}`}
                option={option}
                active={index === active}
                typed={value}
                describe={describe}
                newValueLabel={newValueLabel}
                onChoose={choose}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
